import React from 'react';

import './DemandPlannerComponent.scss';
import 'stylesheets/layout.scss';
import 'stylesheets/fonts.scss';
import { BASE_URL } from '../../constants';
import { createHeadersForApiCall } from '../../helpers/cookies';

import DemandPlannerGraphComponent from './DemandPlannerGraphComponent';
import DemandPlannerBottomComponent from './DemandPlannerBottomComponent';
import { DAYS, deepCloneDemandData } from './demand-planner-helpers';
import { groupUsersByRole } from './demand-role-helpers';
const DAY_MAPPING_TO_NAME = {
  '1': 'Monday',
  '2': 'Tuesday',
  '3': 'Wednesday',
  '4': 'Thursday',
  '5': 'Friday',
  '6': 'Saturday',
  '7': 'Sunday',
};
const DAY_MAPPING_TO_NUM = {
  Monday: '1',
  Tuesday: '2',
  Wednesday: '3',
  Thursday: '4',
  Friday: '5',
  Saturday: '6',
  Sunday: '7',
};

class DemandPlannerComponent extends React.Component {
  state = {
    enabledRoles: [],
    localDemandData: {},
    originalApiDemandData: {},
    demandIds: {},
    isInitialized: false,
    dataProcessed: false,
    isDirty: false,
    isSaving: false,
    showToast: false,
    toastMessage: '',
    toastType: 'success',
    toastAction: null,
    bulkUndoSnapshot: null,
  };

  componentDidMount() {
    const {
      loadAvailabilityDispatch,
      locationId,
      loadLocationRolesDispatch,
      loadDemandDataDispatch,
    } = this.props;
    loadAvailabilityDispatch(locationId);
    loadLocationRolesDispatch(locationId);
    loadDemandDataDispatch(locationId);
  }

  componentDidUpdate(previousProperties) {
    // Location changed — full reset and re-fetch
    if (previousProperties.locationId !== this.props.locationId) {
      this.setState({
        enabledRoles: [],
        localDemandData: {},
        originalApiDemandData: {},
        demandIds: {},
        isInitialized: false,
        dataProcessed: false,
        isDirty: false,
        showToast: false,
        toastMessage: '',
        toastType: 'success',
        toastAction: null,
        bulkUndoSnapshot: null,
      }, () => {
        this.props.loadAvailabilityDispatch(this.props.locationId);
        this.props.loadLocationRolesDispatch(this.props.locationId);
        this.props.loadDemandDataDispatch(this.props.locationId);
      });
      return;
    }

    // Auto-enable first few roles when roles load
    if (
      previousProperties.roles?.length === 0 &&
      this.props.roles?.length > 0
    ) {
      const initialEnabledRoles = this.props.roles
        .map((role) => role.id);
      this.setState({ enabledRoles: initialEnabledRoles });
    }

    // Fix stuck state: roles available but none enabled
    if (
      this.props.roles?.length > 0 &&
      this.state.enabledRoles.length === 0 &&
      this.state.isInitialized
    ) {
      const initialEnabledRoles = this.props.roles
        .map((role) => role.id);
      this.setState({ enabledRoles: initialEnabledRoles });
    }

    // Process demand data once roles fetch completes and demand loading is done
    if (
      !this.props.rolesLoading &&
      !this.props.demandDataLoading &&
      !this.state.dataProcessed
    ) {
      this.loadDemandDataFromAPI();
    }

    // Mark initialized when roles fetch completes and data processed
    if (
      !this.state.isInitialized &&
      !this.props.rolesLoading &&
      this.state.dataProcessed
    ) {
      this.setState({ isInitialized: true });
    }
  }

  // --- Data loading ---

  loadDemandDataFromAPI = () => {
    const { demandData, roles } = this.props;
    if (!demandData || !roles) return;

    // When location has no roles, mark as processed with empty data
    if (roles.length === 0) {
      this.setState({
        localDemandData: {},
        originalApiDemandData: {},
        demandIds: {},
        dataProcessed: true,
      });
      return;
    }

    const originalApiDemandData = {};
    const localDemandData = {};
    const demandIds = {};

    // Initialize all roles with zeros
    roles.forEach((role) => {
      const roleId = role.id;
      localDemandData[roleId] = {};
      DAYS.forEach((day) => {
        localDemandData[roleId][day] = {};
        for (let hour = 0; hour < 24; hour++) {
          localDemandData[roleId][day][
            `${hour.toString().padStart(2, '0')}:00`
          ] = 0;
        }
      });
    });

    // Fill in API data
    demandData.forEach((demandItem) => {
      const roleId = demandItem.tag_id;
      const role = roles.find((r) => r.id === roleId);
      if (!role) return;

      if (demandItem.id) {
        demandIds[roleId] = demandItem.id;
      }

      originalApiDemandData[roleId] = {};
      DAYS.forEach((day) => {
        originalApiDemandData[roleId][day] = {};
        for (let hour = 0; hour < 24; hour++) {
          originalApiDemandData[roleId][day][
            `${hour.toString().padStart(2, '0')}:00`
          ] = 0;
        }
      });

      Object.keys(demandItem.demand_schedule || {}).forEach((dayNumber) => {
        const dayName = DAY_MAPPING_TO_NAME[dayNumber];
        if (!dayName) return;

        const daySchedule = demandItem.demand_schedule[dayNumber];
        Object.keys(daySchedule).forEach((hour) => {
          const timeSlot = `${hour.padStart(2, '0')}:00`;
          const demand = daySchedule[hour];
          originalApiDemandData[roleId][dayName][timeSlot] = demand;
          localDemandData[roleId][dayName][timeSlot] = demand;
        });
      });
    });

    this.setState({
      originalApiDemandData,
      localDemandData,
      demandIds,
      dataProcessed: true,
      isDirty: false,
    });
  };

  // --- User interactions ---

  handleToggleRole = (roleId) => {
    this.setState((previousState) => {
      const isEnabled = previousState.enabledRoles.includes(roleId);
      return {
        enabledRoles: isEnabled
          ? previousState.enabledRoles.filter((id) => id !== roleId)
          : [...previousState.enabledRoles, roleId],
      };
    });
  };

  handleDemandDataUpdate = (newDemandData) => {
    this.setState((previousState) => {
      const updatedLocalData = { ...previousState.localDemandData };

      Object.keys(newDemandData).forEach((roleId) => {
        if (!updatedLocalData[roleId]) {
          updatedLocalData[roleId] = {};
        }

        Object.keys(newDemandData[roleId]).forEach((day) => {
          if (!updatedLocalData[roleId][day]) {
            updatedLocalData[roleId][day] = {};
          }

          Object.keys(newDemandData[roleId][day]).forEach((timeSlot) => {
            updatedLocalData[roleId][day][timeSlot] =
              newDemandData[roleId][day][timeSlot] || 0;
          });
        });
      });

      return {
        localDemandData: updatedLocalData,
        isDirty: true,
      };
    });
  };

  undoDataPointChange = (roleId, day, timeSlot) => {
    this.setState((previousState) => {
      const originalValue =
        previousState.originalApiDemandData[roleId]?.[day]?.[timeSlot] || 0;
      const updatedLocalData = { ...previousState.localDemandData };

      if (!updatedLocalData[roleId]) updatedLocalData[roleId] = {};
      if (!updatedLocalData[roleId][day]) updatedLocalData[roleId][day] = {};
      updatedLocalData[roleId][day][timeSlot] = originalValue;

      return { localDemandData: updatedLocalData };
    });
  };

  resetDemandData = () => {
    this.setState((previousState) => ({
      localDemandData: deepCloneDemandData(previousState.originalApiDemandData),
      isDirty: false,
      bulkUndoSnapshot: null,
    }));
  };

  restoreRoleData = (roleId) => {
    this.setState((previousState) => {
      const updatedLocalData = { ...previousState.localDemandData };
      if (previousState.originalApiDemandData[roleId]) {
        const roleData = previousState.originalApiDemandData[roleId];
        updatedLocalData[roleId] = {};
        for (const day of Object.keys(roleData)) {
          updatedLocalData[roleId][day] = { ...roleData[day] };
        }
      }

      return { localDemandData: updatedLocalData };
    });
  };

  // --- Bulk operations ---

  handleBulkCopyDay = (sourceDay, targetDays) => {
    this.setState((previousState) => {
      const snapshot = deepCloneDemandData(previousState.localDemandData);
      const updatedData = deepCloneDemandData(previousState.localDemandData);

      previousState.enabledRoles.forEach((roleId) => {
        if (!updatedData[roleId]?.[sourceDay]) return;
        targetDays.forEach((targetDay) => {
          updatedData[roleId][targetDay] = {
            ...updatedData[roleId][sourceDay],
          };
        });
      });

      return {
        localDemandData: updatedData,
        bulkUndoSnapshot: snapshot,
        isDirty: true,
      };
    });

    this.showToastNotification(
      `Copied ${sourceDay} to ${targetDays.length} day(s)`,
      'success',
      this.handleBulkUndo
    );
  };

  handleBulkFillRange = (roleId, startHour, endHour, value, targetDays) => {
    this.setState((previousState) => {
      const snapshot = deepCloneDemandData(previousState.localDemandData);
      const updatedData = deepCloneDemandData(previousState.localDemandData);

      targetDays.forEach((day) => {
        if (!updatedData[roleId]) updatedData[roleId] = {};
        if (!updatedData[roleId][day]) updatedData[roleId][day] = {};

        for (let hour = startHour; hour <= endHour; hour++) {
          const timeSlot = `${hour.toString().padStart(2, '0')}:00`;
          updatedData[roleId][day][timeSlot] = value;
        }
      });

      return {
        localDemandData: updatedData,
        bulkUndoSnapshot: snapshot,
        isDirty: true,
      };
    });

    const role = this.props.roles.find((r) => r.id === roleId);
    const roleName = role?.name || `Role ${roleId}`;
    this.showToastNotification(
      `Set ${roleName} to ${value} for ${targetDays.length} day(s)`,
      'success',
      this.handleBulkUndo
    );
  };

  handleBulkUndo = () => {
    this.setState((previousState) => {
      if (!previousState.bulkUndoSnapshot) return {};

      return {
        localDemandData: previousState.bulkUndoSnapshot,
        bulkUndoSnapshot: null,
        isDirty: true,
      };
    });

    this.showToastNotification('Bulk edit undone', 'success');
  };

  // --- Dirty check ---

  isDayDataModified = (localDayData, originalDayData) => {
    if (!originalDayData) {
      return Object.keys(localDayData).some(
        (timeSlot) => localDayData[timeSlot] !== 0
      );
    }

    return Object.keys(localDayData).some(
      (timeSlot) =>
        (localDayData[timeSlot] || 0) !== (originalDayData[timeSlot] || 0)
    );
  };

  hasUnsavedChanges = () => {
    if (!this.state.isDirty) return false;

    const { localDemandData, originalApiDemandData } = this.state;

    for (const roleId of Object.keys(localDemandData)) {
      const localRoleData = localDemandData[roleId];
      const originalRoleData = originalApiDemandData[roleId];

      for (const day of Object.keys(localRoleData)) {
        const modified = this.isDayDataModified(
          localRoleData[day],
          originalRoleData?.[day]
        );
        if (modified) return true;
      }
    }

    // If deep check found no differences, clear the dirty flag
    this.setState({ isDirty: false });
    return false;
  };

  // --- Save ---

  convertDemandDataToApiFormat = (demandData) => {
    const apiPayloads = [];

    this.state.enabledRoles.forEach((roleId) => {
      const role = this.props.roles.find((r) => r.id === roleId);
      if (!role) return;

      const demandSchedule = {};
      Object.keys(demandData[roleId] || {}).forEach((day) => {
        const dayNumber = DAY_MAPPING_TO_NUM[day];
        if (!dayNumber) return;

        demandSchedule[dayNumber] = {};
        Object.keys(demandData[roleId][day] || {}).forEach((timeSlot) => {
          const hour = Number.parseInt(timeSlot.split(':')[0], 10);
          demandSchedule[dayNumber][hour.toString()] =
            demandData[roleId][day][timeSlot] || 0;
        });
      });

      apiPayloads.push({
        tag_id: roleId,
        demand_schedule: demandSchedule,
        is_active: true,
      });
    });

    return apiPayloads;
  };

  saveDemandData = async () => {
    this.setState({ isSaving: true });

    try {
      const { locationId } = this.props;
      const { localDemandData, demandIds } = this.state;
      const apiPayloads = this.convertDemandDataToApiFormat(localDemandData);

      const apiRequests = apiPayloads.map(async (payload) => {
        const roleId = payload.tag_id;
        const demandId = demandIds[roleId];
        const url = demandId
          ? `${BASE_URL}/api/customer/user_role/locations/${locationId}/demands/${demandId}`
          : `${BASE_URL}/api/customer/user_role/locations/${locationId}/demands`;
        const method = demandId ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method,
          headers: createHeadersForApiCall(),
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          const responseText = await response.text();
          throw new Error(
            `HTTP error! status: ${response.status} for role ${roleId}. Response: ${responseText}`
          );
        }

        const responseText = await response.text();
        let result = {};
        if (responseText.trim()) {
          result = JSON.parse(responseText);
        }

        if (method === 'POST' && result.id) {
          this.setState((previousState) => ({
            demandIds: { ...previousState.demandIds, [roleId]: result.id },
          }));
        }

        return { roleId, success: true, result };
      });

      const results = await Promise.all(apiRequests);

      // Snapshot localDemandData as new original
      this.setState((previousState) => ({
        originalApiDemandData: deepCloneDemandData(
          previousState.localDemandData
        ),
        isDirty: false,
        bulkUndoSnapshot: null,
      }));

      this.showToastNotification(
        `Demand forecast saved successfully for ${results.length} roles!`,
        'success'
      );

      // Reload if new demand IDs were created
      const hasNewIds = results.some((r) => r.result && r.result.id);
      if (hasNewIds) {
        this.setState({ dataProcessed: false }, () => {
          this.props.loadDemandDataDispatch(this.props.locationId);
        });
      }
    } catch (error) {
      this.showToastNotification(
        `Error saving demand data: ${error.message}`,
        'error'
      );
    } finally {
      this.setState({ isSaving: false });
    }
  };

  // --- Toast ---

  showToastNotification = (message, type = 'success', action = null) => {
    this.setState({
      showToast: true,
      toastMessage: message,
      toastType: type,
      toastAction: action,
    });
    setTimeout(() => this.hideToastNotification(), 5000);
  };

  hideToastNotification = () => {
    this.setState({
      showToast: false,
      toastMessage: '',
      toastType: 'success',
      toastAction: null,
    });
  };

  // --- Force refresh ---

  forceRefresh = () => {
    const {
      loadAvailabilityDispatch,
      locationId,
      loadLocationRolesDispatch,
      loadDemandDataDispatch,
    } = this.props;
    this.setState(
      {
        enabledRoles: [],
        localDemandData: {},
        originalApiDemandData: {},
        demandIds: {},
        isInitialized: false,
        dataProcessed: false,
        isDirty: false,
        showToast: false,
        toastMessage: '',
        toastType: 'success',
        toastAction: null,
        bulkUndoSnapshot: null,
      },
      () => {
        loadAvailabilityDispatch(locationId);
        loadLocationRolesDispatch(locationId);
        loadDemandDataDispatch(locationId);
      }
    );
  };

  getSaveButtonTitle = (hasChanges, isSaving) => {
    if (isSaving) return 'Saving...';
    if (hasChanges) return 'Save your changes to the demand forecast';

    return 'No changes to save';
  };

  getResetButtonTitle = (hasChanges) => {
    if (hasChanges) return 'Reset all changes to original values';

    return 'No changes to reset';
  };

  // --- Render ---

  render() {
    const { roles, demandDataLoading, demandDataError } = this.props;
    const {
      enabledRoles,
      localDemandData,
      isInitialized,
      isSaving,
      showToast,
      toastMessage,
      toastType,
      toastAction,
      bulkUndoSnapshot,
    } = this.state;
    const isStuck =
      isInitialized && roles?.length > 0 && enabledRoles.length === 0;
    const hasChanges = this.state.isDirty;

    if (!isInitialized || demandDataLoading) {
      return (
        <div className="demand-planner-wrap">
          <div className="demand-planner-loading-overlay">
            <div className="loading-spinner">
              <div className="spinner" />
            </div>
            <div className="loading-text">
              {demandDataLoading
                ? 'Loading demand data...'
                : 'Initializing demand planner...'}
            </div>
            {demandDataError && (
              <div className="loading-error">
                Error: {demandDataError.message}
              </div>
            )}
            {isStuck && (
              <div className="loading-error">
                <div>Component appears to be stuck. Click to refresh:</div>
                <button
                  className="btn btn-primary"
                  style={{ marginTop: '10px' }}
                  type="button"
                  onClick={this.forceRefresh}
                >
                  Refresh Demand Planner
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="demand-planner-wrap">
        <div className="demand-planner-layout">
          <div className="demand-planner-top">
            <div className="graph-actions-section">
              <button
                className={`btn btn-primary ${
                  !hasChanges || isSaving ? 'disabled' : ''
                }`}
                disabled={!hasChanges || isSaving}
                title={this.getSaveButtonTitle(hasChanges, isSaving)}
                type="button"
                onClick={this.saveDemandData}
              >
                {isSaving ? 'Saving...' : 'Save Demand'}
              </button>
              <button
                className={`btn btn-secondary ${hasChanges ? '' : 'disabled'}`}
                disabled={!hasChanges}
                title={this.getResetButtonTitle(hasChanges)}
                type="button"
                onClick={this.resetDemandData}
              >
                Reset to Original
              </button>
              <button className="btn btn-outline" type="button">
                Export Data
              </button>
            </div>
            <DemandPlannerGraphComponent
              apiDemandData={localDemandData}
              canBulkUndo={bulkUndoSnapshot !== null}
              demandDataError={demandDataError}
              demandDataLoading={demandDataLoading}
              enabledRoles={enabledRoles}
              locationCategory={this.props.locationData?.category}
              locationId={this.props.locationId}
              originalApiDemandData={this.state.originalApiDemandData}
              roles={roles}
              usersByRole={groupUsersByRole(this.props.allUsers, roles)}
              onBulkCopyDay={this.handleBulkCopyDay}
              onBulkFillRange={this.handleBulkFillRange}
              onBulkUndo={this.handleBulkUndo}
              onDemandDataUpdate={this.handleDemandDataUpdate}
              onGenerateSuccess={this.forceRefresh}
            />
          </div>
          <div className="demand-planner-bottom">
            <DemandPlannerBottomComponent
              roles={roles}
              enabledRoles={enabledRoles}
              usersByRole={groupUsersByRole(this.props.allUsers, roles)}
              onToggleRole={this.handleToggleRole}
            />
          </div>
        </div>

        {showToast && (
          <div className={`demand-planner-toast ${toastType}`}>
            <div className="toast-content">
              <div className="toast-icon">
                {toastType === 'success' ? '\u2713' : '\u2715'}
              </div>
              <div className="toast-message">
                {toastMessage || 'No message provided'}
              </div>
              {toastAction && (
                <button
                  className="toast-action"
                  type="button"
                  onClick={() => {
                    toastAction();
                    this.hideToastNotification();
                  }}
                >
                  Undo
                </button>
              )}
              <button
                className="toast-close"
                type="button"
                onClick={this.hideToastNotification}
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default DemandPlannerComponent;
